import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  UserCircle,
  Award,
  Compass,
  Users,
  ShieldCheck,
  Check,
} from 'lucide-react';
import Logo from '@/components/brand/Logo';
import { cn } from '@/lib/utils';

export type AuthShellVariant =
  | 'login'
  | 'register'
  | 'recovery'
  | 'verify'
  | 'default';

export type AuthShellFeature = {
  icon: LucideIcon;
  title: string;
  description: string;
};

export type AuthShellTrustBox = {
  title: string;
  description: string;
};

export type AuthShellProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  children: React.ReactNode;
  variant?: AuthShellVariant;
  leftEyebrow?: string;
  leftTitle?: React.ReactNode;
  leftDescription?: string;
  leftFeatures?: AuthShellFeature[];
  leftTrustBox?: AuthShellTrustBox;
  leftBenefits?: string[];
};

const defaultFeatureCards: AuthShellFeature[] = [
  {
    icon: UserCircle,
    title: 'Build your professional maritime profile',
    description:
      'Create a detailed profile that highlights your experience, skills and achievements at sea and ashore.',
  },
  {
    icon: Award,
    title: 'Showcase certificates, sea service and experience',
    description:
      'Upload and present your STCW, COC, GMDSS and other maritime credentials in one place.',
  },
  {
    icon: Compass,
    title: 'Discover verified maritime opportunities',
    description:
      'Browse roles from verified ship owners, managers, manning agents and shore employers.',
  },
  {
    icon: Users,
    title: 'Connect with the maritime community',
    description:
      'Join a network built by maritime professionals for career growth and mentorship.',
  },
];

const defaultTrustBox: AuthShellTrustBox = {
  title: 'Your data, your privacy, your control.',
  description:
    'CrewAhead is designed to help maritime professionals manage career information securely and professionally.',
};

export function AuthShell({
  eyebrow,
  title,
  description,
  children,
  variant = 'default',
  leftEyebrow,
  leftTitle,
  leftDescription,
  leftFeatures,
  leftTrustBox,
  leftBenefits,
}: AuthShellProps) {
  const features = leftFeatures ?? defaultFeatureCards;
  const trustBox = leftTrustBox ?? defaultTrustBox;
  const titleNode = leftTitle ?? (
    <>
      Built for the{' '}
      <span className="font-display text-white">Next Watch</span>
    </>
  );
  const descriptionText =
    leftDescription ??
    'Know your next move. Create, manage and grow your maritime career profile with a platform built for seafarers, shore professionals and maritime employers.';

  return (
    <main
      data-variant={variant}
      className={cn(
        'relative grid min-h-screen grid-cols-1 overflow-x-hidden bg-obsidian text-foreground',
        'md:grid-cols-[40%_60%]',
        'lg:grid-cols-[1fr_1fr]'
      )}
    >
      {/* Subtle ambient background */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-void via-obsidian to-void" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_-10%,rgba(0,243,255,0.06),transparent_50%)]" />

      {/* Left panel — brand story */}
      <section
        aria-label="CrewAhead overview"
        className="relative z-10 order-2 flex flex-col justify-center px-6 py-12 md:order-none md:px-10 lg:px-16 xl:px-20"
      >
        <div className="mx-auto max-w-lg md:mx-0">
          <p className="text-xs font-semibold uppercase tracking-widest text-neon-cyan">
            {leftEyebrow ?? 'CrewAhead Maritime Platform'}
          </p>
          <h2 className="mt-4 text-balance text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">
            {titleNode}
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-ash sm:text-lg">
            {descriptionText}
          </p>

          {leftBenefits ? (
            <ul className="mt-8 space-y-3" aria-label="CrewAhead trust highlights">
              {leftBenefits.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-neon-cyan/10 text-neon-cyan">
                    <Check className="size-3.5" aria-hidden="true" />
                  </span>
                  <span className="text-sm text-foreground sm:text-base">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {features.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.title}
                    className="rounded-xl border border-border/50 bg-panel p-4 shadow-sm transition-colors hover:border-border hover:bg-panel-hover"
                  >
                    <div className="mb-3 inline-flex size-10 items-center justify-center rounded-lg bg-neon-cyan/10 text-neon-cyan">
                      <Icon className="size-5" aria-hidden="true" />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {card.title}
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-ash">
                      {card.description}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 rounded-xl border border-neon-cyan/10 bg-panel p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-neon-cyan/10 text-neon-cyan">
                <ShieldCheck className="size-4" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {trustBox.title}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-ash">
                  {trustBox.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Right panel — authentication card */}
      <section
        aria-label="Authentication"
        className="relative z-10 order-1 flex flex-col justify-center px-4 py-10 md:order-none sm:px-6 md:px-10 lg:px-16"
      >
        <div className="mx-auto w-full max-w-md">
          <div className="rounded-xl border border-border bg-card p-6 shadow-lg sm:p-8">
            <div className="mb-6">
              <Logo variant="full" size={36} />
            </div>

            <div className="mb-6">
              {eyebrow && (
                <p className="text-xs font-semibold uppercase tracking-widest text-neon-cyan">
                  {eyebrow}
                </p>
              )}
              <h1 className="mt-2 text-2xl font-bold text-foreground sm:text-3xl">
                {title}
              </h1>
              {description && (
                <p className="mt-2 text-sm text-ash">{description}</p>
              )}
            </div>

            {children}
          </div>

          <p className="mt-6 text-center text-xs text-ash">
            © {new Date().getFullYear()} CrewAhead. All rights reserved.
          </p>
        </div>
      </section>
    </main>
  );
}

export default AuthShell;
